import { useEffect, useState } from 'react';
import SettingsService, { SystemSettings } from '../services/settingsService';

export interface UseSystemSettingsReturn {
  settings: SystemSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  isMaintenanceMode: boolean;
  maintenanceMessage: string;
}

export const useSystemSettings = (): UseSystemSettingsReturn => {
  const [settings, setSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedSettings = await SettingsService.getSystemSettings();
      setSettings(fetchedSettings);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch settings';
      setError(errorMessage);
      console.error('Error fetching system settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    SettingsService.clearCache();
    await fetchSettings();
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const parseMaintenanceMode = (value: unknown): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on";
    }
    return false;
  };

  return {
    settings,
    loading,
    error,
    refreshSettings,
    isMaintenanceMode: parseMaintenanceMode(settings.maintenanceMode),
    maintenanceMessage: settings.maintenanceMessage || 'System is currently under maintenance. Please check back later.',
  };
};

export default useSystemSettings;
